import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs';
import {
  StenaFooterComponent,
  StenaToolbarComponent,
  StenaUserMenuComponent,
} from '@app-monorepo/shared/ui/stena-components';
import { environment } from '../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { CurrentUserService } from './services/current-user.service';

interface SubLink {
  path: string;
  label: string;
}

const ADMIN_SUB_LINKS: SubLink[] = [
  { path: '/admin/months', label: 'Months' },
  { path: '/admin/currency', label: 'Currency' },
  { path: '/admin/users', label: 'Users' },
];

const REPORTING_SUB_LINKS: SubLink[] = [
  { path: '/reporting', label: 'Monthly' },
  { path: '/reporting/daily', label: 'Daily' },
];

@Component({
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    StenaToolbarComponent,
    StenaFooterComponent,
    StenaUserMenuComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly msal = inject(MsalService);
  private readonly router = inject(Router);
  private readonly currentUser = inject(CurrentUserService);
  protected readonly title = 'BARE Reporting';
  protected readonly isAdmin = this.currentUser.isAdministrator;

  protected readonly headers = computed<string[]>(() => {
    const base = ['home', 'reporting', 'budget'];
    return this.isAdmin() ? [...base, 'admin'] : base;
  });

  private readonly currentUrl = signal(this.router.url);

  protected readonly subLinks = computed<SubLink[]>(() => {
    const url = this.currentUrl();
    if (url.startsWith('/admin')) return ADMIN_SUB_LINKS;
    if (url === '/reporting' || url.startsWith('/reporting/')) {
      return REPORTING_SUB_LINKS;
    }
    return [];
  });

  protected readonly isAccessDenied = computed(() =>
    this.currentUrl().startsWith('/access-denied')
  );

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
  }

  // stena-user-menu drives its own loginRedirect via MsalBroadcastService.
  // We only need to persist the active account and route logout through MSAL.
  onLoginSuccess(account: AccountInfo) {
    this.msal.instance.setActiveAccount(account);
  }

  onLogout() {
    this.msal.logoutRedirect({
      postLogoutRedirectUri: environment.auth.postLogoutRedirectUri,
    });
  }
}
